import { useCallback, useRef, useState } from 'react'
import { Utils } from '@/utils/Utils'
import { ReturnMonitoringItemDto, ReturnMonitoringSaveRowRequest } from '@/services/return/returnMonitoring/types'
import { EDITABLE_COLUMNS, MESSAGES, SAVE_FIELD_MAX_LENGTHS } from '../constants'

export type EditableColumn = (typeof EDITABLE_COLUMNS)[number]

/** 화면 표시용 행 — DTO + 행키. 업로드로 추가된 행은 `id` 가 없고 `_isNew` 가 선다(저장 시 edited='I'). */
export interface ReturnMonitoringRow extends ReturnMonitoringItemDto {
  _rowKey: string
  _isNew?: boolean
}

export const toRows = (items: ReturnMonitoringItemDto[]): ReturnMonitoringRow[] =>
  items.map((item, index) => ({ ...item, _rowKey: `row-${item.id ?? 'x'}-${index}` }))

const EMPTY_ITEM: ReturnMonitoringItemDto = {
  id: null,
  sprNm: null,
  ordStatus: null,
  clsDate: null,
  rtnDate: null,
  receiptDate: null,
  actReturnDate: null,
  grActDate: null,
  inspectDate: null,
  recStartDate: null,
  orgInvoiceNo: null,
  rtnInvoiceNo: null,
  barcode: null,
  skuId: null,
  rtnQty: null,
  carrier: null,
  claimType: null,
  customerClaim: null,
  centerClaim: null,
  etc1: null,
  companyOrdNo: null,
  companyOrdOptNo: null,
  refOrdNo: null,
  refOrdOptNo: null,
  rtnWhNm: null,
  actFlag: null,
  giOrdNo: null,
  giOrdOptNo: null,
  giWhNm: null,
  giActDate: null,
  brdNm: null,
  gdsNm: null,
  gdsOpt: null,
  trsNm: null,
  trsAddr: null,
  trsPhone: null,
  insBy: null,
  insDate: null,
  updBy: null,
  rtnExcDiv: null,
}

/**
 * 엑셀 업로드 행 — 신규(`I`) 후보. 서버 필드가 아닌 값은 여기서 걸러진다(호출부가 IMPORTABLE_KEYS 로 이미 제한).
 * 행키는 `Utils.uuid()`(MAlert 등 레포 전역이 쓰는 방식)로 만든다 — 모듈 스코프 카운터는 HMR 리셋으로
 * 0 부터 다시 시작해 이전 세션에서 만든 행과 키가 겹칠 수 있고, 테스트에서 `Date.now` 를 모킹하면
 * 두 행이 우연히 같은 키를 받을 수도 있다.
 */
export const toUploadRow = (item: Partial<ReturnMonitoringItemDto>): ReturnMonitoringRow => ({
  ...EMPTY_ITEM,
  ...item,
  _rowKey: `upload-${Utils.uuid()}`,
  _isNew: true,
})

/** 편집 여부 — 업로드 행은 항상 dirty, 기존 행은 편집 6종만 비교한다(D14, 출고센터 제외). */
export const isDirtyRow = (row: ReturnMonitoringRow, original?: ReturnMonitoringRow): boolean => {
  if (row._isNew) return true
  if (!original) return false
  return EDITABLE_COLUMNS.some((c) => String(row[c] ?? '') !== String(original[c] ?? ''))
}

/** patch 3상태 — 같으면 `undefined`(JSON 직렬화 시 키 자체가 빠진다) · 비우면 `null` · 바뀌면 값. */
const patchValue = (current: string | null | undefined, original: string | null | undefined): string | null | undefined => {
  const cur = current === '' || current == null ? null : current
  const orig = original === '' || original == null ? null : original
  return cur === orig ? undefined : cur
}

/** 편집분만 골라 `I`/`E` 페이로드로 나눈다(7단계). `E` 는 편집 6종만 patch 로 싣는다. */
export const toSaveRow = (row: ReturnMonitoringRow, original?: ReturnMonitoringRow): ReturnMonitoringSaveRowRequest => {
  if (row._isNew) {
    return {
      edited: 'I',
      // 비면 키를 빼고 BE 가 최상위 shipperCode 를 쓴다(LOGISPRD-14993).
      ...(row.sprNm ? { shipperCode: row.sprNm } : {}),
      clsDate: row.clsDate ?? undefined,
      orgInvoiceNo: row.orgInvoiceNo ?? undefined,
      barcode: row.barcode ?? undefined,
      rtnQty: row.rtnQty ?? undefined,
      carrier: row.carrier ?? undefined,
      rtnInvoiceNo: row.rtnInvoiceNo ?? undefined,
      claimType: row.claimType ?? undefined,
      customerClaim: row.customerClaim ?? undefined,
      centerClaim: row.centerClaim ?? undefined,
      receiptDate: row.receiptDate ?? undefined,
      etc1: row.etc1 ?? undefined,
      companyOrdNo: row.companyOrdNo ?? undefined,
      companyOrdOptNo: row.companyOrdOptNo ?? undefined,
      refOrdNo: row.refOrdNo ?? undefined,
      refOrdOptNo: row.refOrdOptNo ?? undefined,
      rtnWhNm: row.rtnWhNm ?? undefined,
    }
  }

  // 미변경 필드는 키 자체를 만들지 않는다 — `{ field: undefined }` 는 JSON.stringify 가 지워 주지만,
  // 이 함수의 반환값을 직접 단정하는 테스트(hasOwnProperty 기준)가 있어 여기서부터 키를 만들지 않는다.
  const saveRow: ReturnMonitoringSaveRowRequest = { edited: 'E', id: row.id as number }
  const patches: [EditableColumn, string | null | undefined][] = [
    ['orgInvoiceNo', patchValue(row.orgInvoiceNo, original?.orgInvoiceNo)],
    ['rtnInvoiceNo', patchValue(row.rtnInvoiceNo, original?.rtnInvoiceNo)],
    ['claimType', patchValue(row.claimType, original?.claimType)],
    ['customerClaim', patchValue(row.customerClaim, original?.customerClaim)],
    ['centerClaim', patchValue(row.centerClaim, original?.centerClaim)],
    ['barcode', patchValue(row.barcode, original?.barcode)],
  ]
  patches.forEach(([field, value]) => {
    if (value !== undefined) saveRow[field] = value
  })
  return saveRow
}

export interface RequiredField {
  key: keyof ReturnMonitoringItemDto
  title: string
}

/**
 * 저장 전 사전 검증 — 최대 길이(3.3 규칙)·수량 음수·신규 행 필수값. 서버 왕복 없이 선차단한다.
 * requiredFields 는 화면관리 그리드 설정이 필수로 켠 저장 편집 칸이다(LOGISPRD-17253) — 반품수량은 신규 행에서만 편집·저장돼 신규 행만 본다.
 */
export const validateRowForSave = (row: ReturnMonitoringRow, requiredFields: readonly RequiredField[] = []): string[] => {
  const errors: string[] = []
  Object.entries(SAVE_FIELD_MAX_LENGTHS).forEach(([field, max]) => {
    const value = row[field as EditableColumn]
    if (typeof value === 'string' && value.length > max) errors.push(`${field} 는 ${max}자를 넘을 수 없습니다.`)
  })
  // clsDate 는 저장 API(3.3)가 yyyyMMdd 8자 정확 일치를 요구한다. excelImport 가 정규화를 시도하지만
  // 파싱 불가능한 값은 원문 그대로 넘어오므로 여기서 형식을 최종 확인해 서버 400 대신 여기서 막는다.
  if (row.clsDate && !/^\d{8}$/.test(row.clsDate)) errors.push('마감일자(clsDate)는 yyyyMMdd 8자리여야 합니다.')
  // 신규 행 원송장번호·바코드는 설정과 무관하게 막는다 — BE 가 비면 묶음 전체를 거부한다(SaveReturnMonitoringService.kt:184-185)
  if (row._isNew) {
    if (!row.orgInvoiceNo) errors.push('원송장번호는 필수입니다.')
    if (!row.barcode) errors.push('바코드는 필수입니다.')
  }
  requiredFields.forEach(({ key, title }) => {
    if (key === 'rtnQty' && !row._isNew) return
    if (String(row[key] ?? '').trim() === '') errors.push(MESSAGES.requiredMissing(title))
  })
  if (row.rtnQty != null && row.rtnQty < 0) errors.push('반품수량은 0 이상이어야 합니다.')
  return errors
}

export interface SaveTargets {
  payload: ReturnMonitoringSaveRowRequest[]
  targets: ReturnMonitoringRow[]
  errors: string[]
}

/**
 * 그리드 행 편집 상태 — 원본 대비 diff 와 저장 payload 생성을 소유한다.
 * 조회 결과는 페이지가 `setFromItems` 로 밀어 넣는다(React Query 성패를 페이지가 함께 다뤄야 하므로
 * 훅 내부 useEffect 로 items 를 구독하지 않는다).
 */
export function useReturnMonitoringRows() {
  const [rows, setRows] = useState<ReturnMonitoringRow[]>([])
  const originalRef = useRef<Map<string, ReturnMonitoringRow>>(new Map())

  const setFromItems = useCallback((items: ReturnMonitoringItemDto[]) => {
    const next = toRows(items)
    setRows(next)
    originalRef.current = new Map(next.map((r) => [r._rowKey, { ...r }]))
  }, [])

  const clear = useCallback(() => {
    setRows([])
    originalRef.current = new Map()
  }, [])

  /** 업로드 반영 — 신규 행을 맨 앞에 얹고, 전체 선택에 쓸 행키를 돌려준다(1.0 동작: 업로드 후 전 행 선택). */
  const addUploadedRows = useCallback((uploaded: Partial<ReturnMonitoringItemDto>[]): string[] => {
    const newRows = uploaded.map(toUploadRow)
    setRows((prev) => [...newRows, ...prev])
    return newRows.map((r) => r._rowKey)
  }, [])

  /**
   * `core.Table onRowsChange` — 표가 돌려준 행을 키로 찾아 교체한다(목록 검색으로 가려진 행 보존).
   *
   * skuId 는 조회 응답에서만 채워지는 읽기 전용 칸이다. 바코드가 원본과 다르면 옛 바코드의 skuId 가
   * 남지 않게 비우고, 원본 바코드로 되돌리면 원본 skuId 를 되살린다. 업로드 행은 원본이 없어 그대로 둔다.
   */
  const onRowsChange = useCallback((next: ReturnMonitoringRow[]) => {
    setRows((prev) => {
      const byKey = new Map(next.map((r) => [r._rowKey, r]))
      return prev.map((r) => {
        const changed = byKey.get(r._rowKey)
        if (!changed) return r
        const original = originalRef.current.get(r._rowKey)
        if (!original) return changed
        return { ...changed, skuId: changed.barcode === original.barcode ? original.skuId : null }
      })
    })
  }, [])

  /**
   * 선택과 무관하게 편집된 행이 하나라도 있는지 — 저장 대상이 비었을 때 안내 문구를 고르는 데만 쓴다
   * (LOGISPRD-12614). 목록 검색·페이징으로 가려진 행도 `rows` 에 남아 있으므로 함께 센다.
   */
  const hasDirtyRows = useCallback(() => rows.some((r) => isDirtyRow(r, originalRef.current.get(r._rowKey))), [rows])

  /** 선택된 행 중 실제 편집분만 골라 저장 payload 를 만든다. 사전검증 오류가 있으면 `errors` 에 담는다. */
  const buildSavePayload = useCallback(
    (selectedKeys: string[], requiredFields: readonly RequiredField[] = []): SaveTargets => {
      const selected = new Set(selectedKeys)
      const targets = rows.filter((r) => selected.has(r._rowKey) && isDirtyRow(r, originalRef.current.get(r._rowKey)))
      const errors = targets.flatMap((r) => validateRowForSave(r, requiredFields))
      const payload = targets.map((r) => toSaveRow(r, originalRef.current.get(r._rowKey)))
      return { payload, targets, errors }
    },
    [rows],
  )

  return { rows, setFromItems, clear, addUploadedRows, onRowsChange, hasDirtyRows, buildSavePayload }
}
